/**
 * The device's storage (IndexedDB): the replica's metadata in one record, bookings one per record
 * (a sync writes only what is new), own pending changes one per record (a tap writes one small record).
 */
import type { Access, Booking, Op, ReplicaData } from "@lagerlog/core";

export interface Config {
  /** The fixed access of the spike; empty when the device is signed in with an account (session cookie). */
  token: string;
  workspace: string;
  /** With an account: the workspace meant, sent along with every request. */
  workspaceId?: string;
  /** "business" or "private": what the app calls the workspace. */
  kind?: string;
  /** With an account: who is signed in; every booking carries it. */
  userId?: string;
  /** Names of the people of the workspace by account id, kept for showing them offline. */
  people?: Record<string, string>;
  lagers: string[];
  /** With an account: role and granted storages as the server last said (F20); missing: everything. */
  access?: Access;
  device: string;
  /** Protocol of the data on this device (PROTOCOL in packages/core/src/migrate-ml.ts); missing: 1, wines in bottles. */
  protocol?: number;
}

export type ReplicaMeta = Omit<ReplicaData, "bookings" | "pending">;

const DB_NAME = "lagerlog";

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      db.createObjectStore("meta");
      db.createObjectStore("bookings", { keyPath: "id" });
      db.createObjectStore("pending", { autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

export interface Stored {
  config: Config | undefined;
  meta: ReplicaMeta | undefined;
  bookings: Booking[];
  pending: { key: number; op: Op }[];
}

export async function readAll(): Promise<Stored> {
  const db = await openDb();
  const tx = db.transaction(["meta", "bookings", "pending"], "readonly");
  const meta = tx.objectStore("meta");
  const pendingStore = tx.objectStore("pending");
  const [config, replica, bookings, keys, ops] = await Promise.all([
    request(meta.get("config") as IDBRequest<Config | undefined>),
    request(meta.get("replica") as IDBRequest<ReplicaMeta | undefined>),
    request(tx.objectStore("bookings").getAll() as IDBRequest<Booking[]>),
    request(pendingStore.getAllKeys() as IDBRequest<number[]>),
    request(pendingStore.getAll() as IDBRequest<Op[]>),
  ]);
  return { config, meta: replica, bookings, pending: keys.map((key, i) => ({ key, op: ops[i]! })) };
}

export async function saveConfig(config: Config) {
  const db = await openDb();
  const tx = db.transaction("meta", "readwrite");
  tx.objectStore("meta").put(config, "config");
  await done(tx);
}

/** Adds one own change; returns its key (to delete it once confirmed). */
export async function addPending(op: Op): Promise<number> {
  const db = await openDb();
  const tx = db.transaction("pending", "readwrite");
  const key = await request(tx.objectStore("pending").add(op) as IDBRequest<number>);
  await done(tx);
  return key;
}

/** After a sync or load: metadata, new bookings (or all, replacing), confirmed changes removed. */
export async function saveSync(meta: ReplicaMeta, bookings: Booking[], replaceBookings: boolean, confirmedKeys: number[]) {
  const db = await openDb();
  const tx = db.transaction(["meta", "bookings", "pending"], "readwrite");
  tx.objectStore("meta").put(meta, "replica");
  const store = tx.objectStore("bookings");
  if (replaceBookings) store.clear();
  for (const b of bookings) store.put(b);
  const pending = tx.objectStore("pending");
  for (const key of confirmedKeys) pending.delete(key);
  await done(tx);
}

export async function wipe() {
  const db = await openDb();
  const tx = db.transaction(["meta", "bookings", "pending"], "readwrite");
  for (const name of ["meta", "bookings", "pending"]) tx.objectStore(name).clear();
  await done(tx);
}
